// ---------------------------------------------------------------------------
// The small things every part of the reveal uses.
//
// They are here because each of them is read by more than one of the files
// beside it, not because they belong together by subject. `STAGE_FILL` is on
// every frame the stage can show; the identity note explains a prop that every
// chip on the screen passes, in three different files; `AskableWord` is how a
// word on the stage is asked about, and every file that draws a word draws one;
// `SlideSwap` wraps whatever the stage is showing; `useSecondsUntil` is the
// footer's countdown and nothing else's, but it is the one clock rule this
// screen has and it belongs where somebody looking for it will find it;
// `ReasonChip` is drawn by the leaderboard rows and by the AI shadow list.
//
// A second copy of any of them would eventually disagree with the first — which
// is exactly what `STAGE_FILL` exists to stop, since a frame that scrolls the
// page instead of itself is the bug this whole screen was rebuilt around.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { DefinitionWord } from '../../../components/round/WordDefinition.js';
import { serverNow } from '../../../store/gameStore.js';
import type { RevealView } from './view.js';

/** Fills the stage, and scrolls itself rather than the page if it ever cannot. */
export const STAGE_FILL = 'h-full min-h-0 overflow-y-auto';

// --- A word you can ask about ----------------------------------------------

/**
 * A word on the stage, as a control that asks what it means — or, when nothing
 * on this stage may be asked about, exactly the word it was before.
 *
 * The three ways in, and the reason nothing dismisses the answer, are on
 * `components/round/WordDefinition.tsx`. This adds two things to that, and they
 * are the reason it exists rather than every frame importing `DefinitionWord`
 * itself.
 *
 * **When there is nothing to ask, there is no button.** A round with no
 * `revealedAt` has put nothing in front of the room, so the endpoint refuses
 * every word of it, and a button whose only outcome is a refusal is the same
 * mistake as the overrule the conundrum does not offer. The word is still drawn,
 * with the same face — the caller's `className` is what draws it either way, so
 * the two branches are the same pixels.
 *
 * **`marked={false}`: no mark on the reveal, and this reverses what shipped.**
 * Every askable word wore an inset shadow under it, a hairline that reads as a
 * curved underline at these sizes. Isaac asked for it gone, scoped to this
 * screen, and the reveal is where it costs the most: the stage is a handful of
 * large mono words that the room is watching together, and a rule under every
 * one of them is the same clutter complaint that took the handle and the
 * ruled-out row's red outline off this screen. **The record page keeps it** —
 * that is a dense table where the words are the content and nothing else on the
 * page says they can be pressed.
 *
 * **What it costs, said plainly: a phone loses the only hint there was.** There
 * is no hover on a phone, so the mark was the whole affordance for a touch
 * device, and without it an askable word on the reveal looks exactly like a word.
 * Three things make that acceptable and none of them makes it free. The words are
 * still real buttons — tap, keyboard focus, and a screen reader all still reach
 * them, since only the paint went. A tap costs nothing and undoes nothing, and
 * the reveal is a screen people press at anyway. And the record page, which is
 * where a game is read afterwards, still teaches the gesture. What is genuinely
 * lost is discovery for somebody who only ever plays and never opens a record:
 * they will find this by accident or not at all. If that turns out to matter, the
 * fix is a hint somewhere on the reveal rather than the rule under every word.
 *
 * `word` is what is ASKED, and it is compared against `view.pickedWord` as
 * given. Every caller passes it uppercase, which is how the reveal draws words
 * anyway; the endpoint normalizes, so case is nothing but a matter of the two
 * sides of `picked` agreeing.
 */
export function AskableWord({
  view,
  word,
  className,
  children,
}: {
  view: RevealView;
  word: string;
  className?: string;
  children: ReactNode;
}) {
  if (view.definitionGameId === null) return <span className={className}>{children}</span>;

  return (
    <DefinitionWord
      word={word}
      picked={view.pickedWord === word}
      // The button itself, because the answer is placed against the word's own
      // rect now rather than dropped into a slot under the stage.
      onPick={(anchor) => view.onPickWord(word, anchor)}
      marked={false}
      className={className}
    >
      {children}
    </DefinitionWord>
  );
}

/**
 * `dense` is for the scores, and it is not a style choice. A nine-player
 * standing needs every pixel a phone has, and the padding around a list is the
 * cheapest thing on the screen to give up. Switched here rather than passed as a
 * class, because two padding utilities on one element leave which of them wins
 * up to stylesheet order.
 */

// --- Who, without the paperwork --------------------------------------------

/**
 * Takes the human identity slot off every chip on the reveal's stage.
 *
 * **The reveal is a list you read at a glance while the room talks, and
 * `@isaac` is not what you are reading it for.** The complaint was that the
 * panel looked cluttered on a phone and the handle was the first thing named. A
 * leaderboard row is who, what they played and what it scored; the handle is
 * identity plumbing, and it belongs where somebody is deciding who they are
 * playing with rather than in a list scrolling past during a reveal.
 *
 * **Everywhere on this screen, not only on a phone**, and the reason is that a
 * duplicate display name is *suffixed* rather than refused — `uniqueDisplayName`
 * in the engine — so two names in one room can never be the same, and the
 * disambiguation a handle would buy is already bought. What a breakpoint would
 * cost is real: a room watches one reveal from a laptop and five phones at once,
 * and a host reading a row out loud has to be looking at the row everybody else
 * is looking at.
 *
 * **The `guest` and `signed in` tags go with it, and that is a separate
 * decision that lands the same way.** They are the same slot as the handle and
 * mutually exclusive with it, so suppressing only `@isaac` would leave a badge
 * that appears on exactly the players who have no account and on nobody else —
 * a mark for having not signed up, in the middle of the round's payoff. None of
 * the three says anything about the round.
 *
 * **The bot badge and the model id stay**, which is why this is a `:not()` and
 * not a blanket. "You lost to an AI" and "you lost to Opus" are different facts
 * and both are about how the answer on the row was produced.
 *
 * It is applied once, on the stage, so it reaches the closing standing as well —
 * those rows are `ScoreRow`'s and this screen never renders their chips itself.
 *
 * ---
 *
 * **This is the reconciliation point, and it is expected to stop being a class.**
 * `PlayerChip` is gaining a prop that suppresses the slot properly, from another
 * pass that owns that file. Until it lands this rides on `data-player-slot`,
 * which the chip already stamps on all four of its slots. Two things make that
 * safe rather than lucky: the chip's height is a `min-h` per size and the slot is
 * not what sets it, so hiding the slot cannot stagger a row against its
 * neighbour; and `a reveal row draws no handle` in the test file fails loudly if
 * the attribute is ever renamed. When the prop exists, delete this constant and
 * pass the prop — one edit, one place.
 */
// `REVEAL_IDENTITY` was here: a descendant selector on the stage that hid every
// `data-player-slot` but the bot's, standing in for a `PlayerChip` prop that did
// not exist yet. The prop landed as `showIdentity`, and every chip on this
// screen — its own, and `ScoreRow`'s on the standing — now passes it explicitly.
// A prop is the better answer for the reason the note here always said: a class
// that reaches into another component's DOM works right up until that component
// renames an attribute, and nothing would have failed loudly.

// --- Motion ----------------------------------------------------------------

/**
 * One slide replacing another.
 *
 * The point is to say *replaced*, not *changed*: without a transition, answer 3
 * arriving where answer 2 was reads as answer 2 having been edited. It enters
 * from the side it came from, so stepping back looks like stepping back.
 *
 * Mounted with a `key` per slide so the entry state resets by remounting rather
 * than by an effect that would have to flash the old state first. Opacity and
 * transform only: neither is a layout property, so nothing around the stage can
 * be moved by a slide arriving. Under reduced motion the utilities drop the
 * transition and the slide simply appears.
 */
export function SlideSwap({ direction, children }: { direction: 1 | -1; children: ReactNode }) {
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setSettled(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const entry = direction === 1 ? 'translate-x-3' : '-translate-x-3';

  return (
    <div
      className={`h-full transition duration-300 ease-out ${
        settled ? 'translate-x-0 opacity-100' : `${entry} opacity-0`
      }`}
    >
      {children}
    </div>
  );
}

/**
 * Whole seconds until an absolute deadline, repainted twice a second.
 *
 * Read against `serverNow()` and never `Date.now()`: this number is a promise
 * about when the game moves on, and a device clock a few minutes out would make
 * the screen lie about it.
 */
export function useSecondsUntil(at: number | null): number | null {
  const [, repaint] = useState(0);

  useEffect(() => {
    if (at === null) return;
    const timer = setInterval(() => repaint((count) => count + 1), 500);
    return () => clearInterval(timer);
  }, [at]);

  if (at === null) return null;
  return Math.max(0, Math.ceil((at - serverNow()) / 1000));
}

// --- A reason, in one chip -------------------------------------------------

/**
 * A short reason, beside the word it is about.
 *
 * **No fill.** A row is already a wash over the frame on the spotlit and leading
 * states, and a translucent chip inside a translucent row composites deeper than
 * either was solved for — see the palette note in CLAUDE.md. A hairline and
 * coloured text sit on the row's own surface and cost nothing.
 *
 * `shrink-0` and `whitespace-nowrap` together are the answer to the complaint
 * that `Not in the dictionary` and `Disallowed` ran out of their box: the chip is
 * never the thing that gives way. The word beside it truncates instead, and a
 * truncated word is still legible where half a reason is not readable at all.
 */
export function ReasonChip({
  tone,
  children,
}: {
  tone: 'alert' | 'accent' | 'dim';
  children: ReactNode;
}) {
  const skin =
    tone === 'alert'
      ? 'border-alert/50 text-alert'
      : tone === 'accent'
        ? 'border-accent/55 text-accent'
        : 'border-line text-ink-dim';

  return (
    <span
      className={`shrink-0 rounded-chip border px-1.5 py-px font-mono text-[10px] font-semibold tracking-[0.08em] whitespace-nowrap uppercase ${skin}`}
    >
      {children}
    </span>
  );
}
